import React, { useState, useEffect, createContext, useContext } from 'react';
import authService, {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  User,
} from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  register: (data: RegisterRequest) => Promise<AuthResponse>;
  login: (data: LoginRequest) => Promise<AuthResponse>;
  logout: () => void;
  isAuthenticated: boolean;
  updateCompanyName: (data: UpdateCompanyRequest) => Promise<UpdateCompanyResponse>;
  updatePassword: (data: UpdatePasswordRequest) => Promise<UpdatePasswordResponse>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(authService.getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const register = async (data: RegisterRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.register(data);
      setUser(response.data.user);
      return response;
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (data: LoginRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(data);
      setUser(response.data.user);
      return response;
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
  };

  const updateCompanyName = async (data: UpdateCompanyRequest) => {
    setLoading(true);
    try {
      const response = await authService.updateCompanyName(data);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (data: UpdatePasswordRequest) => {
    setLoading(true);
    try {
      const response = await authService.updatePassword(data);
      return response;
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated: !!user,
    updateCompanyName,
    updatePassword,
    clearError,
  };

  return React.createElement(AuthContext.Provider, { value: value }, children);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
